import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ConditionListEditor from "./ConditionListEditor";
import RemoveIconButton from "./RemoveIconButton";
import { axisValueOption, axisValueOptions, type LabelNamesByAxis } from "./axisValueLabels";
import {
  literalValue,
  type Combinator,
  type ConditionNode,
  type RuleActions,
  type WizardParameter,
  type WizardRule,
  type WizardValue,
} from "./clearanceRulesConfig";
import ValueField from "./ValueField";
import ValueOptionContent from "./ValueOptionContent";
import type { LidarClearanceAxis, LidarClearanceColorScale, LidarType } from "../schemas";
import t from "../translations";

type Props = {
  rule: WizardRule;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  axisColorScales: Map<number, LidarClearanceColorScale>;
  lidarTypes: LidarType[];
  parameters: WizardParameter[];
  hasDuplicateConditions: boolean;
  hasDuplicateActions: boolean;
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggleExpanded: () => void;
  onChange: (rule: WizardRule) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

function ActionValueSummary({
  axisName,
  value,
  axes,
  labelNames,
  axisColorScales,
}: {
  axisName: string;
  value: WizardValue;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  axisColorScales: Map<number, LidarClearanceColorScale>;
}) {
  if (value.kind === "param") return <Typography variant="body2">{`$${value.name}`}</Typography>;
  if (value.value === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const axis = axes.find((candidate) => candidate.name_en === axisName);
  if (!axis) return <Typography variant="body2">{value.value}</Typography>;
  const option = axisValueOption(labelNames, axis, value.value, axisColorScales.get(axis.id));
  return <ValueOptionContent option={option} />;
}

function RuleTitle({
  rule,
  axes,
  labelNames,
  axisColorScales,
}: {
  rule: WizardRule;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  axisColorScales: Map<number, LidarClearanceColorScale>;
}) {
  if (rule.actions.size === 0) {
    return <Typography variant="body2">{t.RuleTitlePlaceholder}</Typography>;
  }
  const entries = [...rule.actions.entries()];
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
      {entries.map(([axisName, value], index) => (
        <Stack key={axisName} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          {/* Real space character - CSS gap alone wouldn't show up in text content. */}
          <Typography variant="body2">{`${axisName}: `}</Typography>
          <ActionValueSummary
            axisName={axisName}
            value={value}
            axes={axes}
            labelNames={labelNames}
            axisColorScales={axisColorScales}
          />
          {index < entries.length - 1 && (
            <Typography variant="body2" color="text.secondary">
              ,
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export default function RuleCard({
  rule,
  axes,
  labelNames,
  axisColorScales,
  lidarTypes,
  parameters,
  hasDuplicateConditions,
  hasDuplicateActions,
  expanded,
  isFirst,
  isLast,
  onToggleExpanded,
  onChange,
  onRemove,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: Props) {
  const setConditions = (children: ConditionNode[]) =>
    onChange({ ...rule, root: { ...rule.root, children } });

  const setCombinator = (combinator: Combinator) =>
    onChange({ ...rule, root: { ...rule.root, combinator } });

  const updateActions = (mutate: (actions: RuleActions) => void) => {
    const actions = new Map(rule.actions);
    mutate(actions);
    onChange({ ...rule, actions });
  };

  const toggleAxis = (axisName: string, checked: boolean) =>
    updateActions((actions) =>
      checked ? actions.set(axisName, literalValue()) : actions.delete(axisName)
    );

  const isIncomplete =
    rule.actions.size === 0 ||
    [...rule.actions.values()].some((value) => value.kind === "literal" && value.value === null);

  const highlightColor = isIncomplete
    ? "error"
    : hasDuplicateConditions || hasDuplicateActions
      ? "warning"
      : null;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggleExpanded}
      data-testid="rule-card"
      sx={
        highlightColor
          ? {
              bgcolor: (theme) => alpha(theme.palette[highlightColor].main, 0.08),
              borderLeft: "3px solid",
              borderColor: `${highlightColor}.main`,
            }
          : undefined
      }
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          sx={{ width: "100%", justifyContent: "space-between", alignItems: "center", pr: 1 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {isIncomplete && (
              <Tooltip title={t.IncompleteRuleWarning}>
                <ErrorOutlineIcon
                  color="error"
                  fontSize="small"
                  data-testid="rule-card-incomplete-warning"
                />
              </Tooltip>
            )}
            {hasDuplicateConditions && (
              <Tooltip title={t.DuplicateConditionsRuleWarning}>
                <WarningAmberIcon
                  color="warning"
                  fontSize="small"
                  data-testid="rule-card-duplicate-conditions-warning"
                />
              </Tooltip>
            )}
            {hasDuplicateActions && (
              <Tooltip title={t.DuplicateActionsRuleWarning}>
                <WarningAmberIcon
                  color="warning"
                  fontSize="small"
                  data-testid="rule-card-duplicate-actions-warning"
                />
              </Tooltip>
            )}
            <RuleTitle rule={rule} axes={axes} labelNames={labelNames} axisColorScales={axisColorScales} />
          </Stack>
          <Stack direction="row" onClick={(event) => event.stopPropagation()}>
            {[
              { icon: <ArrowUpwardIcon fontSize="small" />, label: t.MoveRuleUpButton, onClick: onMoveUp, disabled: isFirst },
              { icon: <ArrowDownwardIcon fontSize="small" />, label: t.MoveRuleDownButton, onClick: onMoveDown, disabled: isLast },
              { icon: <ContentCopyIcon fontSize="small" />, label: t.DuplicateRuleButton, onClick: onDuplicate, disabled: false },
            ].map(({ icon, label, onClick, disabled }) => (
              <IconButton key={label} size="small" aria-label={label} disabled={disabled} onClick={onClick}>
                {icon}
              </IconButton>
            ))}
            <RemoveIconButton aria-label={t.RemoveRuleButton} onClick={onRemove} />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="subtitle2" gutterBottom>
          {t.ConditionsLabel}
        </Typography>
        <ConditionListEditor
          nodes={rule.root.children}
          combinator={rule.root.combinator}
          parameters={parameters}
          lidarTypes={lidarTypes}
          onChangeNodes={setConditions}
          onChangeCombinator={setCombinator}
          requireAtLeastOne
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
          {t.ActionsLabel}
        </Typography>
        <Stack spacing={0.5}>
          {axes.map((axis) => {
            const checked = rule.actions.has(axis.name_en);
            const value = rule.actions.get(axis.name_en) ?? literalValue();
            return (
              <Stack
                key={axis.id}
                direction="row"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(event) =>
                        toggleAxis(axis.name_en, event.target.checked)
                      }
                    />
                  }
                  label={axis.name_en}
                  sx={{ width: 220 }}
                />
                {checked && (
                  <ValueField
                    label={t.SeverityValueLabel}
                    value={value}
                    parameters={parameters}
                    onChange={(updated) =>
                      updateActions((actions) => actions.set(axis.name_en, updated))
                    }
                    literalOptions={axisValueOptions(labelNames, axis, axisColorScales.get(axis.id))}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
